import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import PlayingCard from './PlayingCard';

/**
 * DealtCard — a playing card that (1) slides in from the shoe (top-right of
 * the table) and (2) flips from face-down to face-up shortly after landing.
 *
 * Props:
 *  - card:     2-char card code like "AS", "TH". When null and faceDown is
 *              true, stays face-down (dealer's hole card).
 *  - faceDown: if true, the card never flips (dealer's hidden card).
 *  - reveal:   toggle from parent — when it flips from false→true, the card
 *              performs the front-reveal flip. Used for the delayed dealer
 *              hole-card reveal.
 */
export default function DealtCard({ card, faceDown = false, reveal = true, delay = 0, muted = false }) {
    const [flipped, setFlipped] = useState(false);

    // Operator-supplied card-deal sound — plays as each face-up card lands.
    useEffect(() => {
        if (muted || faceDown) return undefined;
        const t = setTimeout(() => {
            try { const a = new Audio('/games/blackjack/audio/card_deal.wav'); a.volume = 0.85; a.play().catch(() => {}); } catch { /* ignore */ }
        }, Math.max(0, delay * 1000));
        return () => clearTimeout(t);
    }, [delay, muted, faceDown]);

    // Land, then flip after the slide finishes so it feels like the dealer
    // is turning the card over.
    useEffect(() => {
        if (faceDown || !reveal) { setFlipped(false); return undefined; }
        const t = setTimeout(() => setFlipped(true), 260 + (delay * 1000));
        return () => clearTimeout(t);
    }, [faceDown, reveal, card, delay]);

    const showFront = flipped && !faceDown && reveal;

    return (
        <motion.div
            initial={{ x: 140, y: -110, opacity: 0, rotate: 12, scale: 0.85 }}
            animate={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 1 }}
            transition={{ type: 'spring', stiffness: 280, damping: 26, mass: 0.72, delay }}
            style={{
                perspective: '900px',
                filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.55))',
            }}
        >
            <div
                className="relative w-12 h-[72px] md:w-[70px] md:h-[100px]"
                style={{
                    transformStyle: 'preserve-3d',
                    transition: 'transform 420ms cubic-bezier(.22,1,.36,1)',
                    transform: showFront ? 'rotateY(0deg)' : 'rotateY(180deg)',
                }}
            >
                {/* Face-up (front) */}
                <div
                    className="absolute inset-0"
                    style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                >
                    <PlayingCard card={card} />
                </div>
                {/* Face-down (back) — rotated 180° so it lives on the reverse side */}
                <div
                    className="absolute inset-0"
                    style={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                    }}
                >
                    <PlayingCard faceDown />
                </div>
            </div>
        </motion.div>
    );
}
